/**
 * Atlas typography from design JSON (semantic.typography.primitives).
 * Source: Atlas – Figma tokens; meta.version 1.0.0.
 *
 * Semantic roles are built from `typographySemanticPrimitiveKeys` so each role
 * always resolves to the matching Figma primitive (same object reference).
 *
 * Figma exports `fontSize`, `lineHeight`, and `letterSpacing` as numbers in px;
 * use `textStyleCss` or `atlasTypographyLengthToPx` when emitting CSS.
 *
 * Example (primitives):
 * ```tsx
 * const Title = styled.h1`
 *   font-family: ${(p) => p.theme.atlas.typography.titleH1.fontFamily};
 *   font-size: ${(p) => p.theme.atlas.typography.titleH1.fontSize}px;
 *   font-weight: ${(p) => p.theme.atlas.typography.titleH1.fontWeight};
 *   line-height: ${(p) => p.theme.atlas.typography.titleH1.lineHeight}px;
 *   letter-spacing: ${(p) => p.theme.atlas.typography.titleH1.letterSpacing}px;
 * `;
 * ```
 *
 * Example (semantic roles):
 * ```tsx
 * const Title = styled.h1`
 *   font-family: ${(p) => p.theme.atlas.typography.semantic.headingH1.fontFamily};
 *   font-size: ${(p) => p.theme.atlas.typography.semantic.headingH1.fontSize}px;
 *   ...
 * `;
 * ```
 */

import { typographySemanticFromMap } from './typographySemanticFromMap';

export const plusJakartaFontFamilyStack =
  '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export type AtlasTypographyToken = {
  fontFamily: string;
  fontWeight: number;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
};

export const typography = {
  titleH1: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 600,
    fontSize: 30,
    lineHeight: 38,
    letterSpacing: 0,
  },
  titleH2: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 600,
    fontSize: 26,
    lineHeight: 34,
    letterSpacing: 0,
  },
  titleH3: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 600,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: 0,
  },
  titleH4: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 600,
    fontSize: 18,
    lineHeight: 28,
    letterSpacing: 0.2,
  },
  textBody: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0.2,
  },
  textMd: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0.2,
  },
  textSm: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 0.3,
  },
  emphasis: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0.2,
  },
  button: {
    fontFamily: plusJakartaFontFamilyStack,
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 25.5,
    letterSpacing: 0.01,
  },
} as const satisfies Record<string, AtlasTypographyToken>;

export type AtlasTypographyPrimitives = typeof typography;

export const typographySemanticPrimitiveKeys = {
  headingH1: 'titleH1',
  headingH2: 'titleH2',
  headingH3: 'titleH3',
  headingH4: 'titleH4',
  body: 'textBody',
  bodySmall: 'textMd',
  caption: 'textSm',
  emphasis: 'emphasis',
  button: 'button',
} as const;

export const typographySemanticSourceByDotNotation = {
  'heading.h1': typographySemanticPrimitiveKeys.headingH1,
  'heading.h2': typographySemanticPrimitiveKeys.headingH2,
  'heading.h3': typographySemanticPrimitiveKeys.headingH3,
  'heading.h4': typographySemanticPrimitiveKeys.headingH4,
  body: typographySemanticPrimitiveKeys.body,
  'body.small': typographySemanticPrimitiveKeys.bodySmall,
  caption: typographySemanticPrimitiveKeys.caption,
  emphasis: typographySemanticPrimitiveKeys.emphasis,
  button: typographySemanticPrimitiveKeys.button,
} as const;

export const typographySemantic = typographySemanticFromMap(
  typography,
  typographySemanticPrimitiveKeys,
);

export type AtlasTypographySemantic = typeof typographySemantic;
